import { useEffect, useId, useLayoutEffect } from "react";

import { FILE_EXTENSIONS, FILE_SIZES } from "@/constants/file";

import { FileInput } from "@components/FileInput";
import { useToastStore } from "@components/PersistentToast/store/toast";

import { FileList } from "./FileList";
import {
  selectHasBusyRows,
  selectShouldMinimise,
  useBundleUploads,
} from "./store/useBundleUploads";
import { bundleToastTitle } from "./utils/bundleToastTitle";

// Reads the store at fire time: the listener is registered once, so a
// captured value would go stale.
const warnWhileBusy = (event: BeforeUnloadEvent) => {
  if (!selectHasBusyRows(useBundleUploads.getState())) return;
  event.preventDefault();
  event.returnValue = "";
};

/**
 * MDRS-550 (AP-13): the bundle toast's only content. The add-files bar and the
 * list share one panel, so opening the picker again — or another case's
 * recovered tasks arriving — never replaces rows that are still uploading or
 * polling.
 */
export const BundleUploadPanel = () => {
  // The add-files bar stays up while a case page is on screen, so clicking
  // "Upload case bundle" again never shifts the rows under it.
  const addFilesTarget = useBundleUploads((state) => state.activeTarget);
  const rowCount = useBundleUploads((state) => state.rows.length);
  const title = useBundleUploads((state) => bundleToastTitle(state.rows));
  const shouldMinimise = useBundleUploads(selectShouldMinimise);
  const addFiles = useBundleUploads((state) => state.addFiles);
  const setMinimiseOnDismiss = useToastStore((state) => state.setMinimiseOnDismiss);
  const setTitle = useToastStore((state) => state.setTitle);
  const closeToast = useToastStore((state) => state.closeToast);
  const addFilesLabelId = useId();

  // Closing would unmount in-flight rows (and drop picked files not started
  // yet), so the header minimises instead. Layout effects so the header never
  // paints stale.
  useLayoutEffect(() => {
    setMinimiseOnDismiss(shouldMinimise);
  }, [shouldMinimise, setMinimiseOnDismiss]);

  useLayoutEffect(() => {
    setTitle(title);
  }, [title, setTitle]);

  // Nothing left to show — no uploads and no case page to add files to (its
  // page was left, or the last row went on a page without one): close rather
  // than linger as an empty header. Only this panel's own toast, never
  // another feature's.
  const isEmpty = rowCount === 0 && !addFilesTarget;
  useLayoutEffect(() => {
    if (isEmpty) closeToast();
  }, [isEmpty, closeToast]);

  // A refresh or tab close would abort the upload with no warning.
  useEffect(() => {
    window.addEventListener("beforeunload", warnWhileBusy);
    return () => window.removeEventListener("beforeunload", warnWhileBusy);
  }, []);

  const handleFilesSelected = (files: File[]) => {
    if (!addFilesTarget || files.length === 0) return;
    addFiles(addFilesTarget, files);
  };

  return (
    // One width, whatever the file names: a long one truncates rather than
    // stretching the pop-up across the page. Narrower on small screens.
    <div className="w-[32rem] max-w-full space-y-4">
      {addFilesTarget && (
        <div role="group" aria-labelledby={addFilesLabelId} className="space-y-2">
          <p
            id={addFilesLabelId}
            className="break-words text-xs font-medium text-foreground-muted"
          >
            Add files to: {addFilesTarget.caseLabel ?? "this case"}
          </p>
          <FileInput
            accept={[...FILE_EXTENSIONS.DOCUMENTS]}
            multiple
            allowedTypes={[...FILE_EXTENSIONS.DOCUMENTS]}
            size="sm"
            maxSize={FILE_SIZES.MB_500}
            placeholder="Drag and drop your file here"
            onFilesSelected={handleFilesSelected}
            showFileList={false}
            // The bar stays up between picks: start each pick fresh.
            resetAfterSelect
          />
        </div>
      )}
      <FileList />
    </div>
  );
};
